import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Button } from "../../Components/UI/button";
import { Textarea } from "../../Components/UI/textarea";
import { Input } from "../../Components/UI/input";
import { Label } from "../../Components/UI/label";
import CustomSearchableDropdown from "./CustomSearchableDD";
import { X, Check, ArrowLeft, Trash2 } from "lucide-react";
import { cn } from "../../Lib/Cn";
import { saveMastersConfigAPI } from "../../Modules/Settings/MasterDataAPI";
import useIsMobile from "../../Hooks/useIsMobile";
import { toast } from "sonner";
import { getConfigData } from "../../Utils/Storage";
import { SpeechControls } from "./SpeechControls";

const TemplateEditorDialog = ({ isOpen, onClose, template = null, defaultType = 1 }) => {
    const [isLoading, setIsLoading] = useState(false);
    const isMobile = useIsMobile();
    const [editorData, setEditorData] = useState({
        TMID: 0,
        TN: "",
        T: "",
        Type: defaultType
    });

    const typeOptions = useMemo(() => {
        const defaultStages = ["New Inquiry", "Qualified", "Presentation", "Negotiation", "Confirmation"];
        let names = defaultStages;
        try {
            const configData = getConfigData();
            const threadStages = typeof configData?.ThreadStages === 'string'
                ? JSON.parse(configData.ThreadStages)
                : (configData?.ThreadStages || []);
            const stages = threadStages[0]?.Stages;
            if (Array.isArray(stages) && stages.length > 0) {
                names = defaultStages.map((def, idx) => stages[idx]?.S || def);
            }
        } catch (e) {
            console.error("Failed to parse thread stages:", e);
        }
        return [
            { value: 1, label: "General" },
            ...names.slice(0, 5).map((name, idx) => ({
                value: idx + 2,
                label: name
            }))
        ];
    }, [isOpen]);

    // Initialize editor data when template prop changes
    useEffect(() => {
        if (template) {
            setEditorData({ ...template, Type: template.Type || defaultType });
        } else {
            setEditorData({ TMID: 0, TN: "", T: "", Type: defaultType });
        }
    }, [template, defaultType, isOpen]);

    // Fetch templates to check for duplicates
    const fetchTemplates = () => {
        try {
            const configData = getConfigData();
            const tmps = configData?.WspTemplates;
            const data = typeof tmps === 'string' ? JSON.parse(tmps) : (tmps || []);
            return data;
        } catch (e) {
            console.error("Failed to parse templates:", e);
            return [];
        }
    };

    const handleSaveTemplate = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!editorData.TN || !editorData.T || !editorData.Type) {
            toast.error('Please fill in all fields.');
            return;
        }

        // Check for duplicate template names
        const templates = fetchTemplates();
        const isDuplicate = templates.some(template =>
            template.TN.trim().toLowerCase() === editorData.TN.trim().toLowerCase() &&
            template.TMID !== editorData.TMID
        );

        if (isDuplicate) {
            toast.error("Template with this name already exists.");
            return;
        }

        setIsLoading(true);

        try {
            const actType = editorData.TMID === 0 ? "add" : "update";

            // Save to API
            await saveMastersConfigAPI("templates", actType, editorData);

            toast.success(actType === "add" ? 'Template Added.' : 'Template Updated.');

            // Close the editor dialog
            onClose();

        } catch (error) {
            console.error("Failed to save template", error);
            toast.error('Failed to save template.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTemplate = async (e, tmid) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!tmid) return;

        setIsLoading(true);
        try {
            await saveMastersConfigAPI("templates", "delete", { TMID: tmid });
            onClose();
            toast.success('Template Deleted.');
        } catch (error) {
            console.error("Failed to delete template", error);
            toast.error('Failed to delete template.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        // Reset editor data
        setEditorData({ TMID: 0, TN: "", T: "", Type: defaultType });
        onClose();
    };

    const renderHeader = () => (
        <DialogHeader>
            {isMobile && (
                <div className="sm:hidden flex items-center justify-between w-full h-8">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <DialogClose className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                            <ArrowLeft className="h-6 w-6" onClick={handleClose} />
                            <span className="sr-only">Back</span>
                        </DialogClose>
                        <DialogTitle className="text-lg font-medium">
                            <div className="truncate">
                                {editorData.TMID === 0 ? "Add Template" : "Edit Template"}
                            </div>
                        </DialogTitle>
                    </div>
                    {editorData.TMID !== 0 && (<Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 p-0"
                        onClick={(e) => handleDeleteTemplate(e, editorData.TMID)}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>)}
                </div>
            )}
            {!isMobile && (
                <>
                    <div className="flex items-center gap-2">
                        <DialogTitle>
                            {editorData.TMID === 0 ? "Add Template" : "Edit Template"}
                        </DialogTitle>
                    </div>

                    <div className="flex items-center gap-1">
                        {editorData.TMID !== 0 && (<Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0 text-text-color sm:hover:bg-gray-200"
                            onClick={(e) => handleDeleteTemplate(e, editorData.TMID)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>)}

                        <DialogClose asChild onClick={onClose}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-sub-text-color">
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close All</span>
                            </Button>
                        </DialogClose>
                    </div>
                </>
            )}
        </DialogHeader>
    );

    const renderEditor = () => (
        <div className="flex flex-col p-4 gap-4 h-full overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="mb-2 block text-sm font-medium">Template Name</Label>
                    <Input
                        value={editorData.TN}
                        onChange={(e) => setEditorData(prev => ({ ...prev, TN: e.target.value }))}
                        placeholder="e.g., Friendly Introduction"
                        className="sahi-input"
                    />
                </div>
                <div>
                    <Label className="mb-2 block text-sm font-medium">Stage Type</Label>
                    <CustomSearchableDropdown
                        fieldID="templateStageType"
                        options={typeOptions}
                        value={editorData.Type || 1}
                        onSelect={(val) => setEditorData(prev => ({ ...prev, Type: val || 1 }))}
                        placeholder="Select Stage Type"
                        searchPlaceholder="Search stage type..."
                        noSort={true}
                        cancellable={false}
                        emptyMessage="No stage types found."
                        mobileDropdownText="Select Stage Type"
                    />
                </div>
            </div>
            <div className="flex-1">
                <Label className="mb-2 block text-sm font-medium">Content</Label>
                <div className="relative">
                    <Textarea
                        id="template-editor-content"
                        value={editorData.T}
                        onChange={(e) => setEditorData(prev => ({ ...prev, T: e.target.value }))}
                        onFocus={(e) => e.stopPropagation()}
                        placeholder="e.g., Hello, this is Amit from SahiClub. I'm reaching out to briefly introduce our services and see if this would be a good time to connect."
                        className="h-[200px] resize-none sahi-input text-sm pr-20"
                        style={{ fieldSizing: "content" }}
                        maxLength={1500}
                    />
                    <SpeechControls
                        targetId="template-editor-content"
                        value={editorData.T}
                        onChange={(val) => setEditorData(prev => ({ ...prev, T: val }))}
                        isMobile={isMobile}
                    />
                </div>
                {editorData.T.length >= 450 && (
                    <span className="text-xs text-gray-500 mt-2">
                        {1500 - editorData.T.length} characters left
                    </span>
                )}
            </div>
        </div>
    );

    const renderFooter = () => (
        <div className="p-4 border-t flex justify-start gap-3">
            <Button
                className="sahi-primary-button gap-2"
                type="button"
                onClick={handleSaveTemplate}
                disabled={isLoading || !editorData.TN || !editorData.T || !editorData.Type}
            >
                {isLoading ? "Saving..." : (
                    <>
                        <Check className="h-4 w-4" />
                        Save Template
                    </>
                )}
            </Button>
            <Button
                variant="outline"
                onClick={handleClose}
                type="button"
                disabled={isLoading}
                className="sahi-secondary-button"
            >
                Cancel
            </Button>
        </div>
    );

    const handleContentClick = (e) => {
        e.stopPropagation();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose} modal={true}>
            <style>{`
                [data-radix-portal]:has([data-radix-popper-content-wrapper]) {
                    z-index: 10050 !important;
                }
                [data-radix-popper-content-wrapper] {
                    z-index: 10050 !important;
                    pointer-events: auto !important;
                }
                [data-radix-popper-content-wrapper] > div {
                    z-index: 10050 !important;
                    pointer-events: auto !important;
                }
            `}</style>
            <DialogContent
                className={cn(
                    "p-0 gap-0 overflow-hidden flex flex-col sahi-radius z-[100]",
                    isMobile
                        ? "max-h-[100vh] h-full w-full rounded-none"
                        : "sm:max-w-2xl h-[70vh]"
                )}
                onClick={handleContentClick}
                onPointerDownOutside={(e) => e.preventDefault()}
            >
                <div className="flex flex-col h-full">
                    {renderHeader()}
                    <div className="flex-1 overflow-hidden relative">
                        {renderEditor()}
                    </div>
                    {renderFooter()}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TemplateEditorDialog;